import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  factoriesListFactoryLlmModels,
  factoriesUpdateFactoryLlmModels,
  organizationsCreateBillingPortalSession,
  organizationsCreateHostedCreditCheckout,
  organizationsListByokllmModels,
  organizationsListHostedCreditProducts,
  organizationsQuoteHostedCreditCheckout,
  organizationsUpdateByokllmModels,
  organizationsListOrganizationHostedLlmModels,
  organizationsUpdateOrganizationHostedLlmModels,
  type OrganizationsListOrganizationHostedLlmModelsResponse,
  type OrganizationsCreateHostedCreditCheckoutBody,
} from "@/api-client";
import { getResponseErrorMessage } from "@/lib/errors";
import { withOrganizationHeader } from "@/lib/withOrganizationHeader";

import { useBYOKAccess } from "./useBYOKAccess";

import { factoryQueryKeys } from "./useFactoryData";
import { hostedLLMModelsQueryKey } from "./useHostedLLMModels";

const BYOK_PROVIDERS = ["anthropic", "openai", "openrouter"] as const;

export function organizationHostedLLMModelsQueryKey(organizationId: string) {
  return ["organizations", organizationId, "hosted-models"] as const;
}

export function useOrganizationHostedLLMModels(organizationId: string) {
  return useQuery({
    queryKey: organizationHostedLLMModelsQueryKey(organizationId),
    queryFn: async () => {
      const response = await organizationsListOrganizationHostedLlmModels(
        withOrganizationHeader({ organizationId, path: { id: organizationId } }),
      );
      return response.data ?? {};
    },
    enabled: Boolean(organizationId),
    staleTime: 30 * 1000,
  });
}

export function useUpdateOrganizationHostedLLMModels(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (allowedModels: string[]) => {
      const response = await organizationsUpdateOrganizationHostedLlmModels(
        withOrganizationHeader({ organizationId, path: { id: organizationId }, body: { allowedModels } }),
      );
      return response.data ?? {};
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        organizationHostedLLMModelsQueryKey(organizationId),
        (previous: OrganizationsListOrganizationHostedLlmModelsResponse | undefined) => ({
          ...previous,
          selected: data.selected ?? [],
        }),
      );
      void queryClient.invalidateQueries({ queryKey: organizationHostedLLMModelsQueryKey(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ["organizations", organizationId, "selectable-llm-models"] });
      void queryClient.invalidateQueries({ queryKey: ["organizations", organizationId, "hosted-llm-models"] });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "factories" &&
          query.queryKey[1] === organizationId &&
          query.queryKey[3] === "llm-models" &&
          query.queryKey[5] === "hosted",
      });
    },
  });
}

export function byokLLMModelsQueryKey(organizationId: string, provider: string, factoryId?: string) {
  return ["organizations", organizationId, "byok-models", provider, factoryId ?? ""] as const;
}

export function useBYOKLLMModels(
  organizationId: string | undefined,
  provider: string | undefined,
  enabled: boolean,
  factoryId?: string,
) {
  const access = useBYOKAccess(organizationId);
  return useQuery({
    queryKey: byokLLMModelsQueryKey(organizationId ?? "", provider ?? "", factoryId),
    queryFn: async () => {
      const response = await organizationsListByokllmModels(
        withOrganizationHeader({
          organizationId: organizationId!,
          path: { id: organizationId! },
          query: { provider, factoryId },
        }),
      );
      return response.data ?? {};
    },
    enabled: Boolean(
      organizationId &&
        provider &&
        enabled &&
        access.allowed &&
        (provider !== "custom" || access.customProviderAllowed),
    ),
    staleTime: 30 * 1000,
  });
}

export function useUpdateBYOKLLMModels(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { provider: string; allowedModels: string[] }) => {
      const response = await organizationsUpdateByokllmModels(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId },
          body: { provider: input.provider, allowedModels: input.allowedModels },
        }),
      );
      return response.data ?? {};
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: ["organizations", organizationId, "byok-models", input.provider],
      });
      void queryClient.invalidateQueries({
        queryKey: ["organizations", organizationId, "selectable-llm-models"],
      });
      void queryClient.invalidateQueries({
        predicate: (query) => isFactoryBYOKModelsQuery(query.queryKey, organizationId, input.provider),
      });
    },
  });
}

export function hostedCreditProductsQueryKey(organizationId: string) {
  return ["organizations", organizationId, "hosted-credit-products"] as const;
}

export function useHostedCreditProducts(organizationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: hostedCreditProductsQueryKey(organizationId ?? ""),
    queryFn: async () => {
      const response = await organizationsListHostedCreditProducts(
        withOrganizationHeader({
          organizationId: organizationId!,
          path: { id: organizationId! },
        }),
      );
      return response.data ?? {};
    },
    enabled: Boolean(organizationId && enabled),
    staleTime: 30 * 1000,
  });
}

export function useHostedCreditCheckoutQuote(
  organizationId: string,
  input: { productId: string; creditAmountCents: string } | undefined,
) {
  return useQuery({
    queryKey: [
      "organizations",
      organizationId,
      "hosted-credit-checkout-quote",
      input?.productId,
      input?.creditAmountCents,
    ],
    queryFn: async ({ signal }) => {
      const response = await organizationsQuoteHostedCreditCheckout(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId },
          query: input!,
          signal,
        }),
      );
      return response.data ?? {};
    },
    enabled: Boolean(organizationId && input),
    retry: false,
  });
}

export function useCreateHostedCreditCheckout(organizationId: string) {
  return useMutation({
    mutationFn: async (input: OrganizationsCreateHostedCreditCheckoutBody) => {
      const response = await organizationsCreateHostedCreditCheckout(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId },
          body: input,
        }),
      );
      const checkoutUrl = response.data?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error("Checkout is not available");
      }
      return checkoutUrl;
    },
  });
}

export function useCreateBillingPortalSession(organizationId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await organizationsCreateBillingPortalSession(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId },
          body: {},
        }),
      );
      const portalUrl = response.data?.portalUrl;
      if (!portalUrl) {
        throw new Error("Add hosted credit first.");
      }
      return portalUrl;
    },
  });
}

export function factoryLLMModelsQueryKey(
  organizationId: string,
  factoryId: string,
  provider: string,
  fundingSource: string,
) {
  return [...factoryQueryKeys.detail(organizationId, factoryId), "llm-models", provider, fundingSource] as const;
}

export function useFactoryLLMModels(
  organizationId: string | undefined,
  factoryId: string | undefined,
  provider: string | undefined,
  fundingSource: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: factoryLLMModelsQueryKey(organizationId ?? "", factoryId ?? "", provider ?? "", fundingSource ?? ""),
    queryFn: async () => {
      const response = await factoriesListFactoryLlmModels(
        withOrganizationHeader({
          organizationId: organizationId!,
          path: { factoryId: factoryId! },
          query: { provider, fundingSource },
        }),
      );
      return response.data ?? {};
    },
    enabled: Boolean(organizationId && factoryId && provider && fundingSource && enabled),
    staleTime: 30 * 1000,
  });
}

export function useUpdateFactoryLLMModels(organizationId: string, factoryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { provider: string; fundingSource: string; allowedModels: string[] }) => {
      const response = await factoriesUpdateFactoryLlmModels(
        withOrganizationHeader({
          organizationId,
          path: { factoryId },
          body: {
            provider: input.provider,
            fundingSource: input.fundingSource,
            allowedModels: input.allowedModels,
          },
        }),
      );
      return response.data ?? {};
    },
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: factoryLLMModelsQueryKey(organizationId, factoryId, input.provider, input.fundingSource),
      });
      if (input.fundingSource === "hosted") {
        void queryClient.invalidateQueries({
          queryKey: hostedLLMModelsQueryKey(organizationId, input.provider, factoryId),
        });
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: byokLLMModelsQueryKey(organizationId, input.provider, factoryId),
      });
    },
  });
}

export function useSwitchFactoryModelSource(organizationId: string, factoryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { source: string; apiKey?: string; baseUrl?: string; apiType?: string }) => {
      const response = await fetch(`/api/v1/factories/${factoryId}/model-source`, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-organization-id": organizationId,
        },
        body: JSON.stringify({
          source: input.source,
          apiKey: input.apiKey ?? "",
          baseUrl: input.baseUrl ?? "",
          apiType: input.apiType ?? "",
        }),
      });
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, "Unable to switch the model source."));
      }
      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: factoryQueryKeys.detail(organizationId, factoryId) });
      void queryClient.invalidateQueries({ queryKey: ["organizations", organizationId, "byok-models"] });
      void queryClient.invalidateQueries({ queryKey: ["organizations", organizationId, "selectable-llm-models"] });
    },
  });
}

export { BYOK_PROVIDERS };

export function isFactoryBYOKModelsQuery(queryKey: readonly unknown[], organizationId: string, provider: string) {
  return (
    queryKey[0] === "factories" &&
    queryKey[1] === organizationId &&
    queryKey[3] === "llm-models" &&
    queryKey[4] === provider &&
    queryKey[5] === "byok"
  );
}
